/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ResetPasswordPage
 *
 * Reads uid, token, and user_type from the URL and wires ResetPasswordForm
 * to the backend ResetPasswordAPIView.
 *
 * URL shape:
 *   /reset-password?uid=...&token=...&type=admin
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ResetPasswordForm from "../components/ResetPasswordForm";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import AlertDialog from "../components/common/AlertDialog";
import { canOpenAppLogin, openAppLogin } from "../utils/appDeepLink";
import { getPostAuthDestination } from "../utils/postAuthDestination";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const RESET_PASSWORD_ENDPOINT = "/arcee/auth/password/reset/";

/**
 * arcee-client is deployed once per application, so the app's name comes from config — this
 * portal carries no product-specific strings. Falls back to a generic label if unnamed.
 */
function getAppLabel() {
  return envConfig.appName || "the app";
}

function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState("admin");
  const [applicationId, setApplicationId] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");
  const [fatalError, setFatalError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qUid = params.get("uid");
    const qToken = params.get("token");
    const qType = params.get("type") || "admin";
    const qAppId = params.get("application_id");

    setUid(qUid);
    setToken(qToken);
    setUserType(qType === "application" ? "application" : "admin");
    setApplicationId(qAppId);

    if (!qUid || !qToken) {
      setFatalError(true);
      setAlertTitle("Invalid link");
      setAlertMessage("The password reset link is invalid or incomplete. Please request a new link.");
      setAlertType("error");
      setAlertOpen(true);
    }
  }, [location.search]);

  const handleResetPassword = async (form, setErrors) => {
    if (!uid || !token) {
      setErrors({
        new_password: "Reset link is invalid or missing. Please request a new link.",
      });
      return;
    }

    try {
      setErrors({});

      const resp = await apiHelper.apiCall(RESET_PASSWORD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: uid,
          token: token,
          user_type: userType,
          new_password: form.new_password,
          application_id: applicationId || envConfig.applicationId,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      const message =
        (data && (data.message || data.error)) ||
        "Something went wrong. Please try again.";

      // Handle invalid/expired/unauthorized token cases
      if (
        resp.status === 401 ||
        resp.status === 403 ||
        (data?.error && typeof data.error === "string" && (
          data.error.toLowerCase().includes("invalid") ||
          data.error.toLowerCase().includes("expired") ||
          data.error.toLowerCase().includes("token")
        ))
      ) {
        setAlertTitle("Link Expired");
        setAlertMessage("This reset link is no longer valid. Please try again later.");
        setAlertType("error");
        setAlertOpen(true);
        setFatalError(true);
        return;
      }

      // Generic error fallback
      if (!resp.ok || data?.error) {
        setErrors({
          new_password: message,
        });
        return;
      }

      const successMessage = message || "Your password has been reset successfully.";

      setAlertTitle("Password Reset");
      // App users on Android get handed back to the native app; tell them what the button does.
      setAlertMessage(
        canOpenAppLogin(userType)
          ? `${successMessage} Tap below to open ${getAppLabel()} and sign in with your new password.`
          : successMessage
      );
      setAlertType("success");
      setAlertOpen(true);
      setFatalError(false);
    } catch (err) {
      // Detect 401 or OAuth unauthorized inside APIHelper error
      const msg = String(err?.message || "").toLowerCase();

      if (
        msg.includes("401") ||
        msg.includes("unauthorized") ||
        msg.includes("invalid token") ||
        msg.includes("credentials") ||
        msg.includes("authentication") ||
        msg.includes("expired")
      ) {
        setAlertTitle("Link Expired");
        setAlertMessage("This reset link is no longer valid. Please try again later.");
        setAlertType("error");
        setAlertOpen(true);
        setFatalError(true);
        return;
      }

      // fallback
      setErrors({
        new_password: "Unable to reset password. Please try again.",
      });
    }

};

// A native-app user who just reset their password should land back in the app's login screen
// rather than the web portal's. Only offered on Android, where the intent URL actually resolves.
const appHandoff = !fatalError && alertType === "success" && canOpenAppLogin(userType);

const handleAlertOk = () => {
  setAlertOpen(false);
  if (fatalError) {
    navigate("/forgot-password");
  } else if (appHandoff) {
    // Fired from the OK click so the navigation carries a user gesture — browsers block
    // un-gestured custom-scheme navigation, which is why this is a button and not a redirect.
    openAppLogin();
  } else {
    navigate(getPostAuthDestination());
  }
};

return (
  <>
    <ResetPasswordForm onSubmit={handleResetPassword} />

    <AlertDialog
      open={alertOpen}
      title={alertTitle}
      message={alertMessage}
      type={alertType}
      okLabel={appHandoff ? `Open ${getAppLabel()}` : "OK"}
      showCancel={false}
      onOk={handleAlertOk}
      onCancel={() => setAlertOpen(false)}
      mini_mode={false}
    />
  </>
);
}

export default ResetPasswordPage;
